(ns practitest.frontend.dashboard-item-settings.events
  (:require [ajax.core :as ajax]
            [re-frame.core :as re-frame]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.components2.dashboard.events :as dashboard-events]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.subs :as global-subs]))

(def ^:private preview-timer (atom nil))

(def db-key :dashboard-item-settings)

(def empty-state
  {:item             {}
   :dashboards       []
   :item-types       []
   :entity-fields    []
   :relevant-fields  []
   :field-pvs        {}
   :instance-fields  []
   :advanced-options {}
   :loading?         false
   :saving?          false
   :embed-code       nil
   :errors           {}
   :dirty?           false})

(defn- save-params [item]
  {:dashboard_item
   {:name                             (:name item)
    :entity                           (:entity item)
    :item_type                        (:item_type item)
    :custom_view_id                   (:custom_view_id item)
    :custom_view_auto_filter          (:custom_view_auto_filter item)
    :dashboard_id                     (:dashboard_id item)
    :x_field_update                   (:x_field_id item)
    :y_field_update                   (:y_field_id item)
    :x_field_criteria_update          (:x_field_criteria item)
    :run_field_filter_update          (:run_field_filter_id item)
    :run_field_filter_criteria_update (:run_field_filter_criteria item)
    ;; The date range select shows 1 week by default - send it, like the legacy form did
    :definition_update                (cond-> (:definition item)
                                        (= "stacked_bar" (:item_type item)) (update :weeks_ago #(str (or % 1))))
    :colors                           (:colors item)
    :cell_width                       (:cell_width item)
    :cell_height                      (:cell_height item)
    :remove_empty_values              (:remove_empty_values item)
    :percentage_mode                  (:percentage_mode item)}})

(re-frame/reg-fx
 ::navigate!
 (fn [url]
   ;; Replaces the current history entry, so "Save and Close" (history back) after saving a new item
   ;; doesn't return to the stale new-item form
   (.replace js/window.location url)))

(re-frame/reg-fx
 ::navigate-back!
 (fn [fallback-url]
   (if (> (.-length js/window.history) 1)
     (.back js/window.history)
     (set! (.-href js/window.location) fallback-url))))

;; Debounces preview requests while the user is still changing the form
(re-frame/reg-fx
 ::schedule-preview!
 (fn [delay-ms]
   (when @preview-timer (js/clearTimeout @preview-timer))
   (reset! preview-timer
           (js/setTimeout #(re-frame/dispatch [::fetch-preview]) delay-ms))))

(defn- abort-pending-preview! [db]
  (when-let [pending-request (get-in db [db-key :pending-preview])]
    (ajax/abort pending-request)))

(re-frame/reg-event-db
 ::preview-pending
 (fn [db [_ xhrio]]
   (assoc-in db [db-key :pending-preview] xhrio)))

(re-frame/reg-event-fx
 ::fetch-preview
 (fn [{:keys [db]} _]
   (let [item      (get-in db [db-key :item])
         item-type (:item_type item)]
     (when item-type
       (let [project-id (global-subs/get-current-project-id db)
             uri        (broutes/get-url "dashboard_items#preview_data" {:p_id project-id})]
         (abort-pending-preview! db)
         {:http-xhrio {:method          :post
                       :uri             uri
                       :params          (save-params item)
                       :format          (ajax/json-request-format)
                       :response-format (ajax/json-response-format {:keywords? false})
                       :on-request      [::preview-pending]
                       :on-success      [::on-preview-success item-type]
                       :on-failure      [::on-preview-failure]}})))))

(re-frame/reg-event-fx
 ::on-preview-success
 (fn [{:keys [db]} [_ item-type response]]
   {:db       (update db db-key dissoc :pending-preview)
    :dispatch [::dashboard-events/store-preview-data item-type response]}))

(re-frame/reg-event-fx
 ::on-preview-failure
 (fn [{:keys [db]} [_ response]]
   ;; Do not handle if aborted by us
   (when-not (= :aborted (:failure response))
     (let [warning (or (get-in response [:response "warning"])
                       (get-translation "dashboard-item.preview.error" "Preview unavailable"))]
       {:db       (update db db-key dissoc :pending-preview)
        :dispatch [::dashboard-events/store-preview-warning warning]}))))

(re-frame/reg-event-fx
 ::load-existing-preview
 (fn [{:keys [db]} [_ {:keys [item-id dashboard-id item-type]}]]
   (let [project-id (global-subs/get-current-project-id db)
         uri        (broutes/get-url "dashboard_items#show"
                                     {:p_id project-id :id item-id}
                                     {:dashboard_id dashboard-id :preview "true"})]
     (abort-pending-preview! db)
     {:http-xhrio {:method          :get
                   :uri             uri
                   :response-format (ajax/json-response-format {:keywords? false})
                   :on-request      [::preview-pending]
                   :on-success      [::on-existing-preview-success item-type]
                   :on-failure      [::on-preview-failure]}})))

(re-frame/reg-event-fx
 ::on-existing-preview-success
 (fn [{:keys [db]} [_ item-type response]]
   {:db       (update db db-key dissoc :pending-preview)
    :dispatch [::dashboard-events/store-preview-data item-type (assoc response "id" "preview")]}))

(re-frame/reg-event-fx
 ::init
 (fn [{:keys [db]} _]
   (let [project-id    (global-subs/get-current-project-id db)
         item-id       (get-in db [:current-route :path-params :item-id])
         dashboard-id  (some-> (get-in db [:current-route :query-params :dashboard_id]) js/parseInt)]
     {:db (assoc db db-key (assoc empty-state :loading? true))
      :dispatch [::fetch-form-options {:project-id project-id
                                       :item-id item-id
                                       :dashboard-id dashboard-id}]})))

(re-frame/reg-event-db
 ::cleanup
 (fn [db _]
   (abort-pending-preview! db)
   (-> db
       (dissoc db-key)
       (update :dashboard-items dissoc "preview"))))

(re-frame/reg-event-fx
 ::fetch-form-options
 (fn [{:keys [db]} [_ {:keys [project-id item-id dashboard-id]}]]
   (let [query-params (cond-> {} item-id (assoc :item_id item-id))
         uri (broutes/get-url "dashboard_items#form_options" {:p_id project-id} query-params)]
     {:http-xhrio {:method          :get
                   :uri             uri
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::on-form-options-success {:project-id project-id
                                                                :item-id    item-id
                                                                :dashboard-id dashboard-id}]
                   :on-failure      [::on-fetch-failure]}})))

(re-frame/reg-event-fx
 ::on-form-options-success
 (fn [{:keys [db]} [_ {:keys [dashboard-id]} response]]
   (let [item       (or (:item response) (when dashboard-id {:dashboard_id dashboard-id}))
         entity     (:entity item)
         item-type  (:item_type item)
         saved-id   (:id item)
         dispatches (cond-> []
                      entity   (conj [::fetch-entity-fields {:entity entity :item-type item-type}])
                      saved-id (conj [::load-existing-preview {:item-id      saved-id
                                                               :dashboard-id (:dashboard_id item)
                                                               :item-type    item-type}]))]
     (cond-> {:db (update db db-key assoc
                          :dashboards      (:dashboards response)
                          :item-types      (:item_types response)
                          :item            (or item {})
                          :embed-code      (:embed_code item)
                          :relevant-fields (or (:relevant_fields response) [])
                          :loading?        false)}
       (seq dispatches) (assoc :dispatch-n dispatches)))))

(re-frame/reg-event-fx
 ::fetch-entity-fields
 (fn [{:keys [db]} [_ {:keys [entity item-type preview?]}]]
   (let [project-id (global-subs/get-current-project-id db)
         query      (cond-> {:entity entity}
                      item-type (assoc :item_type item-type))
         uri (broutes/get-url "dashboard_items#entity_fields" {:p_id project-id} query)]
     {:http-xhrio {:method          :get
                   :uri             uri
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::on-entity-fields-success preview?]
                   :on-failure      [::on-fetch-failure]}})))

(defn- merge-response-fields [db response]
  (update db db-key assoc
          :entity-fields    (or (:entity_fields response) [])
          :relevant-fields  (or (:relevant_fields response) [])
          :field-pvs        (or (:field_pvs response) {})
          :instance-fields  (or (:instance_fields response) [])
          :advanced-options (or (:advanced_options response) {})))

;; The legacy form's field selects had no empty option, so the browser pre-selected the first one -
;; the selects below do the same while they're empty
(defn uses-field-value? [item]
  (= "gauge" (:item_type item)))

(defn- autoselect-x-field-criteria [db]
  (let [{:keys [field-pvs item]} (get db db-key)]
    (cond-> db
      (and (uses-field-value? item)
           (:x_field_id item)
           (nil? (:x_field_criteria item)))
      (assoc-in [db-key :item :x_field_criteria] (:value (first (get field-pvs (keyword (:x_field_id item)))))))))

(defn- autoselect-fields [db]
  (let [{:keys [entity-fields relevant-fields]} (get db db-key)
        relevant?   #(some #{%} relevant-fields)
        first-field (:value (first entity-fields))
        default     (fn [db k v]
                      (cond-> db
                        (and (some? v) (nil? (get-in db [db-key :item k])))
                        (assoc-in [db-key :item k] v)))]
    (-> db
        (default :x_field_id (when (relevant? "x_field") first-field))
        ;; Group By is stored in y_field too
        (default :y_field_id (when (or (relevant? "y_field") (relevant? "group_by")) first-field))
        autoselect-x-field-criteria)))

;; preview? - the item type was just changed: start its definition with the backend's defaults (sent on
;; save - the backend reads a missing value of a pair as 0 / today), and fetch the preview once the
;; auto-selected fields are in the item. Values edited before the response arrived are kept.
(re-frame/reg-event-fx
 ::on-entity-fields-success
 (fn [{:keys [db]} [_ preview? response]]
   (cond-> {:db (-> db
                    (merge-response-fields response)
                    (cond-> preview?
                      (update-in [db-key :item :definition] #(merge (:default_definition response) %)))
                    autoselect-fields)}
     preview? (assoc :dispatch [::fetch-preview]))))

(re-frame/reg-event-fx
 ::on-fetch-failure
 (fn [{:keys [db]} [_ _response]]
   {:db       (assoc-in db [db-key :loading?] false)
    :dispatch [::notifications/error2
               (get-translation "dashboard-item.notify.load-failed" "Failed to load dashboard item data")]}))

(re-frame/reg-event-fx
 ::update-field
 (fn [{:keys [db]} [_ field value]]
   {:db                 (-> db
                            (assoc-in [db-key :item field] value)
                            (assoc-in [db-key :dirty?] true))
    ::schedule-preview! 600}))

(re-frame/reg-event-fx
 ::update-x-field
 (fn [{:keys [db]} [_ value]]
   {:db                 (-> db
                            (update-in [db-key :item] assoc
                                       :x_field_id       value
                                       :x_field_criteria nil)
                            autoselect-x-field-criteria
                            (assoc-in [db-key :dirty?] true))
    ::schedule-preview! 300}))

(re-frame/reg-event-fx
 ::update-run-field-filter
 (fn [{:keys [db]} [_ value]]
   {:db                 (-> db
                            (update-in [db-key :item] assoc
                                       :run_field_filter_id       value
                                       :run_field_filter_criteria nil)
                            (assoc-in [db-key :dirty?] true))
    ::schedule-preview! 300}))

(re-frame/reg-event-fx
 ::update-instance-filter
 (fn [{:keys [db]} [_ filter-map]]
   {:db                 (-> db
                            (assoc-in [db-key :item :definition :instance_filter] filter-map)
                            (assoc-in [db-key :dirty?] true))
    ::schedule-preview! 300}))

;; Like the legacy form, the date range is limited to 3 months (the select shows months when it's empty)
(defn- limit-date-range [definition]
  (cond-> definition
    (and (= "months" (or (:weeks_or_months definition) "months"))
         (> (js/parseInt (:gaps definition)) 3))
    (assoc :gaps "3")))

(re-frame/reg-event-fx
 ::update-definition
 (fn [{:keys [db]} [_ field value]]
   {:db                 (-> db
                            (assoc-in [db-key :item :definition field] value)
                            (cond-> (#{:gaps :weeks_or_months} field)
                              (update-in [db-key :item :definition] limit-date-range))
                            (assoc-in [db-key :dirty?] true))
    ::schedule-preview! 600}))

(re-frame/reg-event-fx
 ::update-entity
 (fn [{:keys [db]} [_ entity]]
   ;; The preview is cleared below and not re-fetched, so a response still on the way would be stale
   (abort-pending-preview! db)
   {:db       (-> db
                  (update-in [db-key :item] assoc
                             :entity                    entity
                             :item_type                 nil
                             :custom_view_id            nil
                             :custom_view_auto_filter   nil
                             :x_field_id                nil
                             :x_field_criteria          nil
                             :y_field_id                nil
                             :run_field_filter_id       nil
                             :run_field_filter_criteria nil
                             :definition                nil
                             :cell_width                nil
                             :cell_height               nil
                             :colors                    nil
                             :remove_empty_values       nil
                             :percentage_mode           nil)
                  (update db-key assoc
                          :entity-fields    []
                          :relevant-fields  []
                          :instance-fields  []
                          :advanced-options {}
                          :dirty?           true)
                  (update :dashboard-items dissoc "preview"))
    :dispatch [::fetch-entity-fields {:entity entity}]}))

(re-frame/reg-event-fx
 ::update-item-type
 (fn [{:keys [db]} [_ item-type]]
   (let [entity (get-in db [db-key :item :entity])]
     ;; The preview is cleared below and re-fetched only after the entity fields arrive, so a
     ;; response still on the way would show the previous item type until then
     (abort-pending-preview! db)
     {:db       (-> db
                    (update-in [db-key :item] assoc
                               :item_type           item-type
                               :x_field_id          nil
                               :x_field_criteria    nil
                               :y_field_id          nil
                               :definition          nil
                               :cell_width          nil
                               :cell_height         nil
                               :colors              nil
                               :remove_empty_values nil
                               :percentage_mode     nil)
                    (update db-key assoc
                            :relevant-fields  []
                            :advanced-options {}
                            :dirty?           true)
                    (update :dashboard-items dissoc "preview"))
      ;; The fields are auto-selected from this response (when relevant), so the preview is fetched after it
      :dispatch [::fetch-entity-fields {:entity entity :item-type item-type :preview? true}]})))

(re-frame/reg-event-fx
 ::update-custom-view
 (fn [{:keys [db]} [_ view-id auto-filter]]
   {:db                 (-> db
                            (update-in [db-key :item] assoc
                                       :custom_view_id          view-id
                                       :custom_view_auto_filter auto-filter)
                            (assoc-in [db-key :dirty?] true))
    ::schedule-preview! 300}))

(defn relevant-field? [relevant-fields field-name]
  (boolean (some #{field-name} relevant-fields)))

;; Returns [field message] pairs of the missing mandatory fields, in form order
(defn- validate-item [item]
  (for [[field message] [[:dashboard_id (get-translation "dashboard-item.validate.dashboard-tab-required" "Please select a Dashboard Tab")]
                         [:entity (get-translation "dashboard-item.validate.entity-required" "Please select an Entity")]
                         [:item_type (get-translation "dashboard-item.validate.item-type-required" "Please select an Item Type")]]
        :when (nil? (get item field))]
    [field message]))

(re-frame/reg-event-fx
 ::save
 (fn [{:keys [db]} [_ {:keys [close?]}]]
   (let [project-id (global-subs/get-current-project-id db)
         item       (get-in db [db-key :item])
         missing    (validate-item item)]
     (if (seq missing)
       ;; The fields are marked in the form, the toast shows the first one
       {:db       (assoc-in db [db-key :errors] (into {} missing))
        :dispatch [::notifications/error2 (second (first missing))]}
       (let [item-id (:id item)
             edit?   (some? item-id)
             method  (if edit? :put :post)
             uri     (if edit?
                       (broutes/get-url "dashboard_items#update" {:p_id project-id
                                                                  :id item-id})
                       (broutes/get-url "dashboard_items#create" {:p_id project-id}))]
         {:db (assoc-in db [db-key :saving?] true)
          :http-xhrio {:method          method
                       :uri             uri
                       :params          (save-params item)
                       :format          (ajax/json-request-format)
                       :response-format (ajax/json-response-format {:keywords? true})
                       :on-success      [::on-save-success {:project-id project-id :close? close? :new? (not edit?)}]
                       :on-failure      [::on-save-failure]}})))))

(re-frame/reg-event-fx
 ::on-save-success
 (fn [{:keys [db]} [_ {:keys [project-id close? new?]} response]]
   (let [item-id (:id response)
         new-db  (update db db-key assoc
                         :saving?    false
                         :item       response
                         :embed-code (:embed_code response)
                         :errors     {}
                         :dirty?     false)]
     (cond
       close?
       {:db new-db ::navigate-back! (str "/p/" project-id "/dashboards")}

       new?
       ;; After creating a new item, navigate to its edit URL so the URL reflects the saved item
       {:db new-db ::navigate! (str "/p/" project-id "/dashboard_items/" item-id "/edit")}

       :else
       {:db       new-db
        :dispatch [::notifications/success2
                   (get-translation "dashboard-item.notify.saved" "Dashboard item saved successfully")]}))))

(re-frame/reg-event-fx
 ::on-save-failure
 (fn [{:keys [db]} [_ response]]
   {:db       (assoc-in db [db-key :saving?] false)
    :dispatch [::notifications/error2
               (or (get-in response [:response :message])
                   (get-translation "dashboard-item.notify.save-failed" "Failed to save dashboard item"))]}))

(re-frame/reg-event-fx
 ::cancel
 (fn [{:keys [db]} _]
   (let [project-id (global-subs/get-current-project-id db)]
     {::navigate-back! (str "/p/" project-id "/dashboards")})))
